/** Iconos del menú. Van dibujados aquí, en SVG, y no en una tipografía de iconos ni en una
 *  dependencia externa: el sitio es estático, no carga nada de fuera y debe seguir funcionando sin
 *  red. Todos comparten rejilla de 24, trazo de 1,6 y `currentColor`, de modo que heredan el color
 *  del enlace —también el del estado activo— y valen igual en claro y en oscuro.
 *
 *  Son decorativos: el nombre del modo va siempre escrito al lado, así que los SVG se marcan con
 *  aria-hidden y no llevan <title>. Un lector de pantalla lee «Leer», no «icono de libro». */
import type { SVGProps } from 'react';

const base = {
  width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, focusable: false,
} as const;

type P = SVGProps<SVGSVGElement>;

/** Libro abierto: leer el texto. */
export const IconLeer = (p: P) => (
  <svg {...base} {...p}>
    <path d="M12 6.4C10 5 7.6 4.6 4.5 4.8v12.6c3.1-.2 5.5.2 7.5 1.6 2-1.4 4.4-1.8 7.5-1.6V4.8c-3.1-.2-5.5.2-7.5 1.6Z" />
    <path d="M12 6.4v12.6" />
  </svg>
);

/** Mapa plegado: explorar el territorio. */
export const IconExplorar = (p: P) => (
  <svg {...base} {...p}>
    <path d="M9 4.5 3.5 6.7v12.8L9 17.3l6 2.2 5.5-2.2V4.5L15 6.7Z" />
    <path d="M9 4.5v12.8M15 6.7v12.8" />
  </svg>
);

/** Chincheta: los itinerarios son una sucesión de lugares sobre el mapa. */
export const IconItinerarios = (p: P) => (
  <svg {...base} {...p}>
    <path d="M12 21c4.2-4.6 6.3-7.9 6.3-10.6A6.3 6.3 0 0 0 5.7 10.4C5.7 13.1 7.8 16.4 12 21Z" />
    <circle cx="12" cy="10.3" r="2.4" />
  </svg>
);

/** Mando: los retos. Es el icono que pediste; si algún día el mando sugiere demasiado «videojuego»,
 *  aquí se cambia por una diana o una brújula sin tocar nada más. */
export const IconRetos = (p: P) => (
  <svg {...base} {...p}>
    <path d="M8.2 7h7.6a4.7 4.7 0 0 1 4.6 3.8l.8 4.3A2.6 2.6 0 0 1 18.7 18c-.8 0-1.5-.4-2-1l-1-1.3H8.3l-1 1.3c-.5.6-1.2 1-2 1a2.6 2.6 0 0 1-2.5-2.9l.8-4.3A4.7 4.7 0 0 1 8.2 7Z" />
    <path d="M7.1 11v2.4M5.9 12.2h2.4" />
    <circle cx="15.6" cy="11.4" r=".9" fill="currentColor" stroke="none" />
    <circle cx="17.6" cy="13.2" r=".9" fill="currentColor" stroke="none" />
  </svg>
);

/** Lupa: buscar. */
export const IconBuscar = (p: P) => (
  <svg {...base} {...p}>
    <circle cx="10.8" cy="10.8" r="6.3" />
    <path d="m15.4 15.4 4.1 4.1" />
  </svg>
);

/** Birrete: el aula. */
export const IconAula = (p: P) => (
  <svg {...base} {...p}>
    <path d="M12 4.6 2.8 9.1 12 13.6l9.2-4.5Z" />
    <path d="M6.6 11.3v4.4c0 1.6 2.4 2.9 5.4 2.9s5.4-1.3 5.4-2.9v-4.4" />
    <path d="M21.2 9.1v5" />
  </svg>
);

/** Libros apilados: el ejemplar completo, el libro como objeto. */
export const IconVolumen = (p: P) => (
  <svg {...base} {...p}>
    <rect x="3.4" y="5.6" width="4.3" height="13" rx="1" />
    <rect x="9.2" y="5.6" width="4.3" height="13" rx="1" />
    <path d="m15.6 6.9 4 1-2.4 12.7-4-1Z" />
  </svg>
);

/** Círculo con una i: sobre el proyecto, sus fuentes y sus créditos. */
export const IconProyecto = (p: P) => (
  <svg {...base} {...p}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M12 11.2v5" />
    <circle cx="12" cy="8.1" r=".95" fill="currentColor" stroke="none" />
  </svg>
);

/** Tres nodos enlazados: compartir lo que se está viendo. */
export const IconCompartir = (p: P) => (
  <svg {...base} {...p}>
    <circle cx="17.5" cy="5.8" r="2.6" />
    <circle cx="6.5" cy="12" r="2.6" />
    <circle cx="17.5" cy="18.2" r="2.6" />
    <path d="M8.8 10.8 15.2 7.1M8.8 13.2l6.4 3.7" />
  </svg>
);

/** Candado abierto: las licencias libres. Solo se usa en la portada del proyecto. */
export const IconAbierto = (p: P) => (
  <svg {...base} {...p}>
    <rect x="4.5" y="10.6" width="15" height="9.4" rx="2" />
    <path d="M8.4 10.6V7.9a3.6 3.6 0 0 1 7-1.2" />
    <circle cx="12" cy="15.3" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

/** Edificio con frontón y columnas: el patrimonio, en genérico. No representa ningún monumento
 *  concreto; los de verdad están en el mapa y en el facsímil. */
export const IconPatrimonio = (p: P) => (
  <svg {...base} {...p}>
    <path d="M12 3.4 3.4 8h17.2Z" />
    <path d="M5.6 8v9.6M10 8v9.6M14 8v9.6M18.4 8v9.6" />
    <path d="M3.4 17.6h17.2M2.6 20.6h18.8" />
  </svg>
);
